import { useState } from 'react'

import { RadioGroupField, type RadioGroupFieldProps } from '@components/molecules/RadioGroupField'

import type { ComponentDoc, PropDocs } from '../types'

type Choice = 'new' | 'top'

const props: PropDocs<RadioGroupFieldProps<Choice>> = {
  label: {
    type: 'ReactNode',
    required: true,
    description: 'Visible label for the group; names it for screen readers.',
  },
  options: {
    type: 'RadioGroupFieldOption<T>[]',
    required: true,
    description:
      'Each option is { value, label, description?, whenSelected? }. The description becomes the radio’s accessible description; whenSelected renders under the option while it is chosen.',
  },
  value: {
    type: 'T | null',
    required: true,
    description: 'The chosen value, or null when nothing is chosen. Fully controlled.',
  },
  onValueChange: { type: '(value: T) => void', required: true, description: 'Called with the newly chosen value.' },
  appearance: {
    type: "'list' | 'cards' | 'tiles'",
    default: "'list'",
    description:
      'A plain list with descriptions under each label, bordered cards with the description on the right, or small cards in a row. Tiles show whenSelected under the row.',
  },
  name: { type: 'string', description: 'Field name, for react-hook-form. Builds the error and hint ids.' },
  description: { type: 'string', description: 'Hint under the group.' },
  errors: { type: 'FieldErrors', description: 'react-hook-form errors. The error for name shows under the group.' },
  required: { type: 'boolean', default: 'false', description: 'Adds “(required)” to the label.' },
  disabled: { type: 'boolean', default: 'false', description: 'Disables every option.' },
  className: { type: 'string', description: 'Merged with cn on the wrapper.' },
  ref: {
    type: 'Ref<HTMLButtonElement>',
    description: 'Attached to the first radio, so react-hook-form can focus the group when it fails validation.',
  },
}

function Controlled({ appearance }: { appearance?: 'list' | 'cards' }) {
  const [value, setValue] = useState<Choice>('new')
  return (
    <div className="tw:w-full tw:max-w-md">
      <RadioGroupField<Choice>
        label="Open event pages in"
        value={value}
        onValueChange={setValue}
        appearance={appearance}
        options={[
          { value: 'new', label: 'A new tab', description: 'Keeps the supporter’s search open.' },
          { value: 'top', label: 'The same tab', description: 'Replaces the page they are on.' },
        ]}
      />
    </div>
  )
}

function Tiles() {
  const [value, setValue] = useState('24')
  return (
    <div className="tw:w-full tw:max-w-md">
      <RadioGroupField
        appearance="tiles"
        label="Skip supporters contacted in the last"
        value={value}
        onValueChange={setValue}
        options={[
          { value: '24', label: '1 day' },
          { value: '72', label: '3 days' },
          { value: '168', label: '1 week' },
          { value: 'custom', label: 'Custom' },
        ]}
      />
    </div>
  )
}

type Method = 'phone_call' | 'email' | 'text' | 'face_to_face'

function InForm() {
  const [value, setValue] = useState<Method | null>(null)
  return (
    <div className="tw:w-full tw:max-w-md">
      <RadioGroupField<Method>
        name="contactMethod"
        label="Contact method"
        description="How you reached the supporter."
        required
        errors={value ? {} : { contactMethod: { type: 'required', message: 'Contact method is a required field' } }}
        value={value}
        onValueChange={setValue}
        options={[
          { value: 'phone_call', label: 'Phone call' },
          { value: 'email', label: 'Email' },
          { value: 'text', label: 'Text' },
          { value: 'face_to_face', label: 'In person' },
        ]}
      />
    </div>
  )
}

const doc: ComponentDoc<RadioGroupFieldProps<Choice>> = {
  slug: 'radio-group-field',
  name: 'RadioGroupField',
  tier: 'molecule',
  description:
    'One choice from a short list: a labelled Radix radio group with a label and optional description per option. Wraps the RadioGroup atom so every radio group is labelled and described the same way.',
  importStatement: "import { RadioGroupField } from '@components/molecules/RadioGroupField'",
  sourcePath: 'app/frontend/components/molecules/RadioGroupField.tsx',
  props,
  examples: [
    {
      title: 'List',
      render: () => <Controlled />,
      code: `<RadioGroupField
  label="Open event pages in"
  value={value}
  onValueChange={setValue}
  options={[
    { value: 'new', label: 'A new tab', description: 'Keeps the supporter’s search open.' },
    { value: 'top', label: 'The same tab', description: 'Replaces the page they are on.' },
  ]}
/>`,
    },
    {
      title: 'In a form',
      note: 'With name, errors and required it shows “(required)”, the hint and react-hook-form’s error, like a SelectField. Choose an option to clear the error.',
      render: () => <InForm />,
      code: `<RadioGroupField
  name={field.name}
  ref={field.ref}
  label="Contact method"
  description="How you reached the supporter."
  required
  errors={errors}
  value={field.value}
  onValueChange={field.onChange}
  options={contactMethods}
/>`,
    },
    {
      title: 'Cards',
      note: 'For longer choices in dialogs. whenSelected opens extra fields under the chosen card, like a date picker.',
      render: () => <Controlled appearance="cards" />,
      code: `<RadioGroupField appearance="cards" ... />`,
    },
    {
      title: 'Tiles',
      note: 'For short choices like durations. The tiles sit in a row and wrap on narrow screens.',
      render: () => <Tiles />,
      code: `<RadioGroupField
  appearance="tiles"
  label="Skip supporters contacted in the last"
  options={[
    { value: '24', label: '1 day' },
    { value: '72', label: '3 days' },
    { value: '168', label: '1 week' },
    { value: 'custom', label: 'Custom', whenSelected: <TextInput ... /> },
  ]}
  ...
/>`,
    },
  ],
  usage: {
    do: [
      'Use it for two to four mutually exclusive options, all worth seeing at once. From five, use a SelectField.',
      'Keep it past five options when each one opens its own fields with whenSelected, which a select cannot do.',
      'In a react-hook-form form, pass name, errors and required, the same as a SelectField.',
      'Keep option labels short and put the detail in the description.',
      'In a list or cards, indent whenSelected content with tw:ml-7 so it lines up with the option labels.',
    ],
    dont: [
      'Hand-build a RadioGroup with your own labels and ids. The ids here come from useId, so two groups on a page never collide.',
      'Use it for an on/off setting. Use a Switch.',
    ],
  },
  a11y: [
    'The visible label names the radiogroup through aria-labelledby. It is a span, not a <label>, because there is no single control for it to point at.',
    'The hint and the error describe the radiogroup through aria-describedby, and the error is announced when it appears.',
    'Each option label is a <label htmlFor>, so the whole row is clickable, and the description is linked with aria-describedby.',
    'Radix handles roving focus: Tab reaches the group, arrow keys move and select.',
  ],
}

export default doc
